import { Button } from "primereact/button";
import { MouseEvent, useState } from "react";

import { ContentFeedback } from "sj-ui-toolkit/src/client";
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

import ActionBar, { Action } from "../ActionBar/ActionBar";

import { useFlags } from "launchdarkly-react-client-sdk";
import S from "../_Lesson.module.scss";
import { InputTextarea } from "primereact/inputtextarea";

const knowledgeOptions = {
  1: {
    emoticon: ":-(",
    label: "None",
  },
  2: {
    emoticon: ":-\\",
    label: "Little",
  },
  3: {
    emoticon: ":-)",
    label: "Some",
  },
  4: {
    emoticon: ":-D",
    label: "A lot",
  },
  5: {
    emoticon: "B-)",
    label: "So much",
  },
};

interface SurveyProps {
  headerText: string;
  onSubmit: (
    feedback: CombinedFeedback,
    isExiting?: boolean,
    event?: MouseEvent<HTMLButtonElement>
  ) => void;
  isAfterLessonSurvey: boolean;
  hasNextLesson?: boolean;
  isPathCompleted?: boolean;
  displayKnowledgeRating?: boolean;
}

export interface CombinedFeedback extends ContentFeedback {
  knowledgeRating: string | null;
}

const Survey = ({
  headerText,
  onSubmit,
  isAfterLessonSurvey,
  hasNextLesson,
  isPathCompleted,
  displayKnowledgeRating = true,
}: SurveyProps) => {
  const [feedback, setFeedback] = useState<CombinedFeedback>({
    knowledgeRating: null,
    rating: null,
    feedbackText: null,
  });
  const { displayLessonRating } = useFlags();

  const Actions: Action[] = [
    {
      ...(isAfterLessonSurvey && (isPathCompleted || hasNextLesson)
        ? {
            label: isPathCompleted ? "Finish" : "Next Lesson",
            icon: "chevron-right",
            iconPos: "right",
            onClick: event => onSubmit(feedback, false, event),
          }
        : {
            label: "Confirm",
            icon: "check",
            onClick: () => onSubmit(feedback),
          }),
    },
    {
      label: isAfterLessonSurvey ? "Exit" : "Skip",
      severity: "secondary",
      onClick: () => onSubmit(feedback, isAfterLessonSurvey),
    },
  ];

  return (
    <div className="flex flex-col h-full w-full">
      <div
        className={C("flex-1 min-h-0 overflow-y-auto", T.largePaddedContainer)}
        data-testid="lesson-survey-content"
      >
        <h2 className="atlas-h1 mb-8 mt-6" data-testid="lesson-survey-title">
          {headerText}
        </h2>
        {displayKnowledgeRating && (
          <div className="flex-1" data-testid="lesson-survey-knowledge-rating">
            <p className="atlas-h4 mb-4" id="rating-description">
              {isAfterLessonSurvey
                ? "How much do you think you know about this topic now?"
                : "How much do you think you know about this topic?"}
            </p>
            <div
              className="mt-6"
              role="group"
              aria-describedby="rating-description"
              data-testid="lesson-survey-knowledge-rating-options"
            >
              {Object.entries(knowledgeOptions).map(([key, option]) => (
                <Button
                  key={key}
                  severity={feedback.knowledgeRating === key ? undefined : "secondary"}
                  className={S.learningSwingOption}
                  onClick={() => setFeedback({ ...feedback, knowledgeRating: key })}
                  aria-pressed={feedback.knowledgeRating === key}
                  data-testid={`lesson-survey-knowledge-rating-option-${key}`}
                >
                  <span>{option.emoticon}</span>
                  <span>{option.label}</span>
                  <span>{key}</span>
                </Button>
              ))}
            </div>
          </div>
        )}
        {displayLessonRating && isAfterLessonSurvey && (
          <>
            <div className="flex-1 mb-4" data-testid="lesson-survey-feedback">
              <p className="atlas-h4" id="feedback-description">
                What did you think of this lesson?
              </p>
              <div
                className="flex flex-col w-fit"
                role="group"
                aria-describedby="feedback-description"
                data-testid="lesson-survey-feedback-options"
              >
                <div className="-ml-2">
                  {Array.from({ length: 10 }).map((_, idx) => (
                    <Button
                      key={`rating-${idx}`}
                      className={idx >= (feedback?.rating ?? 0) ? "text-muted-aaa" : undefined}
                      icon={idx >= (feedback?.rating ?? 0) ? "pi pi-star" : "pi pi-star-fill"}
                      size="large"
                      onClick={() => setFeedback({ ...feedback, rating: idx + 1 })}
                      aria-label={`Set lesson rating to ${idx + 1}`}
                      data-testid={`lesson-survey-feedback-option-${idx + 1}`}
                      text
                    ></Button>
                  ))}
                </div>
                <div className="flex justify-between w-full">
                  <p className="body-sm text-muted-aaa">Did not like it at all</p>
                  <p className="body-sm text-muted-aaa">Really liked it</p>
                </div>
              </div>
            </div>
            <label
              htmlFor="feedback-textarea"
              className="block font-semibold mb-2"
              data-testid="lesson-survey-feedback-label"
            >
              Additional feedback (optional)
            </label>
            <InputTextarea
              id="feedback-textarea"
              data-testid="lesson-survey-feedback-textarea"
              placeholder="Please share any feedback that you have"
              value={feedback.feedbackText ?? ""}
              onChange={e => setFeedback({ ...feedback, feedbackText: e.target.value })}
              className="w-full"
              rows={4}
            ></InputTextarea>
          </>
        )}
      </div>
      <ActionBar actions={Actions} />
    </div>
  );
};

export default Survey;
